import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { authHeaders } from "../lib/auth";
import { readProfile } from "../lib/profile";
import DailyHydrationCard from "./DailyHydrationCard";
import AbsorptionIntelligenceCard from "./AbsorptionIntelligenceCard";
import SourceTaggingCard from "./SourceTaggingCard";
import CompareMyWatersScreen from "./CompareMyWatersScreen";
import WaterRankingsCard from "./WaterRankingsCard";
import AppHeader from "./AppHeader";
import { useBottleConnection } from "../hooks/useBottleConnection";

// ─── STROKE-BASED SVG ICONS (matching nav bar style) ────────────────────────
function BtlIcon({ name, size = 16, color = "#A6A8AB" }) {
  const s = size;
  const ic = color;
  const icons = {
    droplet: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M12 3C12 3 6 10 6 14C6 17.31 8.69 20 12 20C15.31 20 18 17.31 18 14C18 10 12 3 12 3Z" stroke={ic} strokeWidth="1.5" fill={`${ic}20`}/><path d="M9 14C9 14 10 12 12 12" stroke={ic} strokeWidth="1.2" strokeLinecap="round"/></svg>,
    thermometer: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M12 2C10.34 2 9 3.34 9 5V14.26C7.8 15.08 7 16.44 7 18C7 20.76 9.24 23 12 23C14.76 23 17 20.76 17 18C17 16.44 16.2 15.08 15 14.26V5C15 3.34 13.66 2 12 2Z" stroke={ic} strokeWidth="1.5" fill="none"/><circle cx="12" cy="18" r="2" fill={ic}/><line x1="12" y1="8" x2="12" y2="16" stroke={ic} strokeWidth="1.5" strokeLinecap="round"/></svg>,
    testTube: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M7 2L7 16C7 18.76 9.24 21 12 21C14.76 21 17 18.76 17 16V2" stroke={ic} strokeWidth="1.5" strokeLinecap="round" fill="none"/><line x1="7" y1="2" x2="17" y2="2" stroke={ic} strokeWidth="1.5" strokeLinecap="round"/><line x1="7" y1="13" x2="17" y2="13" stroke={ic} strokeWidth="1.2" strokeLinecap="round" strokeDasharray="2 2"/></svg>,
    battery: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><rect x="2" y="7" width="18" height="10" rx="2" stroke={ic} strokeWidth="1.5" fill="none"/><path d="M22 11V13" stroke={ic} strokeWidth="2" strokeLinecap="round"/><rect x="5" y="10" width="6" height="4" rx="1" fill={`${ic}40`}/></svg>,
    ruler: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><rect x="3" y="5" width="18" height="14" rx="2" stroke={ic} strokeWidth="1.5" fill="none"/><line x1="7" y1="5" x2="7" y2="10" stroke={ic} strokeWidth="1.2" strokeLinecap="round"/><line x1="11" y1="5" x2="11" y2="8" stroke={ic} strokeWidth="1.2" strokeLinecap="round"/><line x1="15" y1="5" x2="15" y2="10" stroke={ic} strokeWidth="1.2" strokeLinecap="round"/><line x1="19" y1="5" x2="19" y2="8" stroke={ic} strokeWidth="1.2" strokeLinecap="round"/></svg>,
    signal: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><line x1="12" y1="14" x2="12" y2="22" stroke={ic} strokeWidth="1.5" strokeLinecap="round"/><path d="M8 10C9.1 8.9 10.5 8 12 8C13.5 8 14.9 8.9 16 10" stroke={ic} strokeWidth="1.5" strokeLinecap="round" fill="none"/><path d="M5 7C7 4.5 9.3 3 12 3C14.7 3 17 4.5 19 7" stroke={ic} strokeWidth="1.5" strokeLinecap="round" fill="none"/><circle cx="12" cy="13" r="1.5" fill={ic}/></svg>,
    target: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke={ic} strokeWidth="1.5" fill="none"/><circle cx="12" cy="12" r="5" stroke={ic} strokeWidth="1.3" fill="none"/><circle cx="12" cy="12" r="1.5" fill={ic}/></svg>,
    hazard: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M12 2L22 20H2L12 2Z" stroke={ic} strokeWidth="1.5" strokeLinejoin="round" fill="none"/><line x1="12" y1="9" x2="12" y2="14" stroke={ic} strokeWidth="1.5" strokeLinecap="round"/><circle cx="12" cy="17" r="1" fill={ic}/></svg>,
    flask: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M9 3V10L4 18C3.5 19 4 20 5 20H19C20 20 20.5 19 20 18L15 10V3" stroke={ic} strokeWidth="1.5" strokeLinejoin="round" fill="none"/><line x1="8" y1="3" x2="16" y2="3" stroke={ic} strokeWidth="1.5" strokeLinecap="round"/><path d="M7 15H17" stroke={ic} strokeWidth="1.2" strokeLinecap="round"/></svg>,
  };
  return <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", verticalAlign: "middle" }}>{icons[name] || null}</span>;
}

// ═══════════════════════════════════════════════════════════════
// BLE PROTOCOL, PP02 V05 (Production Partners, 2026-01-16)
// Service UUID:    0xFFE0
// Write char:      0xFFE2  APP → Device
// Notify char:     0xFFE1  Device → APP
// Packet (TX):     [0x55][CHECKSUM][CMD][0x00][LEN_L][D0..Dn]
// Packet (RX):     [0xAA][CHECKSUM][CMD][LEN_H][LEN_L][D0..Dn]
// Checksum:        (CMD + LEN_H + LEN_L + ΣData) & 0xFF
//
// FIRMWARE REALITY (fw=10, raw-frame diagnosis 2026-07-07, see the Production
// Partners defect report of the same date):
//   0x04 water level = the ONLY trustworthy volume signal. Everything the user
//                      sees about bottle contents is computed from it (BTL_CAL).
//   0x06 volume      = DEFECTIVE (570 on an empty bottle, unstable on a still
//                      one). Never rendered to the user; visible in the debug
//                      panel only.
//   0x07 intake %    = DEFECTIVE counter (creeps sealed, ignores real sips).
//                      Debug panel only.
//   0x05 water temp  = transmits whole degrees C against the deci-degree spec,
//                      so the decoded value is wrong 10x on real hardware.
//                      Real-hardware temp renders as "Calibrating" until the
//                      firmware fix ships. Demo frames are correctly scaled.
// ═══════════════════════════════════════════════════════════════

const POLL_MS    = 2000;
const MAX_HIST   = 60;

// Verified daily total: the SAME source My Hydration reads, so the two screens can
// never tell different stories about the same day.
const API_BASE = "https://generosity-sales-engine-mvp-api.onrender.com";
// Phase 2: identity rides via authHeaders() (X-WTR-Auth session token).
const TODAY_POLL_MS = 12000;
// Display refresh cadence for the pinned water quality result: dynamic, but
// on a 20 s cycle, never per 2 s poll frame (founder decision 2026-07-08).
const DISPLAY_SAMPLE_MS = 20000;

// ═══════════════════════════════════════════════════════════════
// SENSOR DEBUG HELPERS (dev branch, retained through the soak test)
// Purpose: answer sensor questions from real frames, no console needed.
// Removal PR after the day-long soak validation passes; the RX resync fix
// and checksum logging in onNotify are permanent keepers.
// ═══════════════════════════════════════════════════════════════

// Expected sensor set for SMART 1 (22oz/650mL): TDS + Temp + Volume.
const EXPECTED_SENSORS = [
  { type: 0x02, name: "TDS" },
  { type: 0x05, name: "WATER TEMP" },
  { type: 0x06, name: "VOLUME" },
];

function hexType(t) {
  return "0x" + t.toString(16).padStart(2, "0").toUpperCase();
}

// Aggregate per-sensor stats across the telemetry history buffer.
// Answers presence (count vs frames), drift (min/max/span), and trend (series).
function sensorStatsFromHist(hist) {
  const stats = {};
  hist.forEach(h => {
    Object.entries(h.sensors || {}).forEach(([key, sx]) => {
      if (!stats[key]) {
        stats[key] = { key, type: sx.type, label: sx.label, unit: sx.unit, count: 0, min: Infinity, max: -Infinity, last: null, series: [] };
      }
      const st = stats[key];
      st.count += 1;
      st.last = sx.raw;
      if (sx.raw < st.min) st.min = sx.raw;
      if (sx.raw > st.max) st.max = sx.raw;
      st.series.push(sx.raw);
    });
  });
  return stats;
}

// Plain-text report Micah can copy out of Bluefy and paste into a chat.
function buildDebugReport({ tele, hist, log }) {
  const lines = [];
  lines.push("WTR BTL SENSOR DEBUG REPORT");
  lines.push("Generated: " + new Date().toISOString());
  lines.push("");
  if (tele) {
    lines.push("DEVICE: state=" + tele.state + " batt=" + tele.batt + "% charging=" + tele.charging + " fw=" + tele.fw);
  } else {
    lines.push("DEVICE: no parsed telemetry yet");
  }
  lines.push("Frames in history buffer: " + hist.length + " (poll every " + (POLL_MS / 1000) + "s)");
  lines.push("");

  const stats = sensorStatsFromHist(hist);
  const seenTypes = new Set(Object.values(stats).map(s => s.type));

  lines.push("EXPECTED SENSORS (SMART 1: TDS, Water Temp, Volume):");
  EXPECTED_SENSORS.forEach(e => {
    if (seenTypes.has(e.type)) {
      lines.push("  " + hexType(e.type) + " " + e.name + ": PRESENT");
    } else {
      lines.push("  !! " + hexType(e.type) + " " + e.name + ": MISSING FROM ALL FRAMES");
    }
  });
  lines.push("");

  lines.push("SENSOR PRESENCE AND DRIFT:");
  Object.values(stats).sort((a, b) => a.type - b.type).forEach(st => {
    lines.push(
      "  " + hexType(st.type) + " " + st.label +
      ": seen " + st.count + "/" + hist.length +
      "  raw last=" + st.last +
      " min=" + st.min +
      " max=" + st.max +
      " span=" + (st.max - st.min)
    );
    lines.push("    raw series (oldest to newest, last 20): " + st.series.slice(-20).join(", "));
  });
  lines.push("");

  lines.push("RAW LOG (newest first, up to 40 entries):");
  log.slice(0, 40).forEach(e => {
    lines.push("  [" + e.t + "] " + e.msg + (e.hex ? "  " + e.hex : ""));
  });

  return lines.join("\n");
}

// ═══════════════════════════════════════════════════════════════
// WATER QUALITY SCALE (single source for score, band word, and color)
// Score: continuous piecewise-linear curve over TDS anchor points, so
// every ppm moves the score. Replaces the old 4-step staircase that
// scored all of 151 to 300 ppm as an identical 88.
// Bands (founder spec 2026-07-08): 90+ Premium, 80 to 89 Good,
// 70 to 79 Moderate, 60 to 69 Poor, below 60 Very Poor.
// "Unsafe" is retired: TDS is an EPA secondary (aesthetic) standard and
// cannot support a safety claim.
// CANONICAL MAPPING NOTE: routes/absorptionIntelligence.js mirrors
// TDS_SCORE_ANCHORS and scoreFromTdsValue byte for byte. Change both.
// ═══════════════════════════════════════════════════════════════

const TDS_SCORE_ANCHORS = [
  [0, 94], [7, 94], [25, 98], [55, 90], [80, 85], [120, 80], [150, 78],
  [200, 72], [250, 66], [300, 60], [400, 50], [500, 40], [750, 26], [1000, 16], [1500, 10],
];

function scoreBandWord(score) {
  if (score == null) return "";
  if (score >= 90) return "Premium";
  if (score >= 80) return "Good";
  if (score >= 70) return "Moderate";
  if (score >= 60) return "Poor";
  return "Very Poor";
}

function bandColor(score) {
  if (score == null) return "#51B0E6";
  if (score >= 90) return "#4A8A6F";
  if (score >= 80) return "#51B0E6";
  if (score >= 70) return "#C89B3C";
  if (score >= 60) return "#B84A4A";
  return "#7A2E28";
}

// Water quality score. TDS-ONLY on fw=10: the firmware volume field (0x06) is
// defective and water temp (0x05) transmits whole degrees against a deci-degree
// spec (2026-07-07 defect report, defects 1 and 2), so neither may influence a
// consumer-facing score. Re-expand only after Production Partners ship a fix
// and it passes acceptance.
// CANONICAL MAPPING NOTE: routes/absorptionIntelligence.js scoreFromTds()
// mirrors scoreFromTdsValue() byte for byte. If this changes, change that too,
// until the mapping moves fully server-side (the planned single source).
function scoreFromTdsValue(tds) {
  if (tds == null || Number.isNaN(Number(tds))) return null;
  const v = Math.max(0, Number(tds));
  const A = TDS_SCORE_ANCHORS;
  if (v >= A[A.length - 1][0]) return A[A.length - 1][1];
  for (let i = 1; i < A.length; i++) {
    if (v <= A[i][0]) {
      const [x0, y0] = A[i - 1];
      const [x1, y1] = A[i];
      const t = (v - x0) / (x1 - x0 || 1);
      return Math.max(0, Math.min(100, Math.round(y0 + t * (y1 - y0))));
    }
  }
  return A[A.length - 1][1];
}
function waterScore(sensors) {
  return scoreFromTdsValue(sensors.tds?.raw ?? null);
}

// Median of the positive values in a sample window. Used to stabilize the
// captured test TDS against single-frame electrode noise (2026-07-08
// recording: raw TDS swinging 143 to near-zero within seconds on a still
// bottle). Median of real reads, never a fabricated value.
function medianOfPositive(vals) {
  const v = vals.filter(x => x != null && x > 0).sort((a, b) => a - b);
  if (!v.length) return null;
  return v[Math.floor(v.length / 2)];
}

// ═══════════════════════════════════════════════════════════════
// WATER SOURCE TAGGING
// After a test whose source is unknown, the user tags what they filled
// with. Each tag is training data: stored on the entry locally and posted
// to the API (wtr_test_source_labels) with the test's TDS, score, and
// geo/PWSID context, the ground-truth loop for source-aware scoring.
// ═══════════════════════════════════════════════════════════════

// v1.7: the pill taxonomy, brand drawer, insight rotation, and bridge
// content live in lib/sourceTagging.config.js, rendered by
// components/SourceTaggingCard.jsx. The old client-side nearest-neighbor
// suggestSource is retired: suggestions now come from the server's
// per-user pattern rollup (GET /api/bottle/test-source/suggest), which
// also decides when the prompt stops appearing at all.

// ═══════════════════════════════════════════════════════════════
// APP-COMPUTED BOTTLE CONTENTS
// The user-facing volume figure is derived from the ToF level (0x04), the only
// trustworthy volume signal on fw=10. The defective firmware volume (0x06)
// never reaches a card; it survives only in the debug panel and as the
// cumulative_ml reference column in posted events.
// Mirror of CAL.SMART1 in lib/bottleSync.js; the single source of truth for
// these constants moves server-side per the named scale-debt in that file.
// ═══════════════════════════════════════════════════════════════

const BTL_CAL = { ML_PER_MM: 3.1, EMPTY_OFFSET_MM: 17, MAX_LEVEL_MM: 260 };

// The displayed Level number moves only on a real sip or fill: a sustained
// change of at least this many mL. ToF jitter on a still bottle is a few mL
// (2026-07-08 recording: 465 to 471 on a still bottle), so 25 mL separates
// events from noise while still catching a single swig.
const LEVEL_EVENT_ML = 25;

// ═══════════════════════════════════════════════════════════════
// SUBMERSION GATE (2026-07-11, empty-bottle phantom-score incident)
// The TDS electrode only reads while submerged; on a drained bottle the
// firmware carries the last non-zero reading forward, and medianOfPositive
// then discards the honest near-zero frames, so a stale positive becomes
// the "official" result (observed: 62 ppm / score 89 / RO narrative on an
// empty bottle, 30+ minutes after draining). No water quality claim of any
// kind may be made below this settled level. 100 mL is PROVISIONAL pending
// the PP02 probe-depth figure from Production Partners; when that lands,
// set this to (probe immersion depth in mm + 5 mm margin) * ML_PER_MM.
// MIRROR NOTE: routes/absorptionIntelligence.js CONFIG.MIN_TEST_LEVEL_ML
// must match this value. Change both.
// ═══════════════════════════════════════════════════════════════
const MIN_TEST_LEVEL_ML = 100;

// Settled level for gating decisions: median of the recent positive level
// frames plus the current frame. Same math as the level event gate, shared
// so the test gate and the display gate cannot disagree.
function settledLevelMl(histArr, tele) {
  return medianOfPositive(
    (histArr || []).slice(-3).map(h => levelToMl(h.sensors?.waterLevel?.val))
      .concat([levelToMl(tele?.sensors?.waterLevel?.val)])
  );
}

function levelToMl(levelMm) {
  if (levelMm == null) return null;
  const mm = parseFloat(levelMm);
  // Dropout frames (0) and slosh spikes render as "no reading", never as a number.
  if (!Number.isFinite(mm) || mm <= 0 || mm > BTL_CAL.MAX_LEVEL_MM) return null;
  return Math.max(0, Math.round((mm - BTL_CAL.EMPTY_OFFSET_MM) * BTL_CAL.ML_PER_MM));
}

// ═══════════════════════════════════════════════════════════════
// SPARKLINE (light mode)
// ═══════════════════════════════════════════════════════════════

function Sparkline({ data = [], color = "#51B0E6", w = 80, h = 28 }) {
  if (data.length < 2) return <div style={{ width: w, height: h }} />;
  const min = Math.min(...data), max = Math.max(...data), rng = max - min || 1;
  const pts = data
    .map((v, i) => `${(i / (data.length - 1)) * w},${h - ((v - min) / rng) * (h - 4) - 2}`)
    .join(" ");
  return (
    <svg width={w} height={h} style={{ display: "block", overflow: "visible" }}>
      <polyline
        points={pts}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinejoin="round"
        strokeLinecap="round"
        opacity="0.85"
      />
    </svg>
  );
}

// ═══════════════════════════════════════════════════════════════
// OURA-STYLE SCORE RING (Hydration Score layout: eyebrow above a large
// status-colored score, status word beneath, blue intelligence subline)
// ═══════════════════════════════════════════════════════════════

function ScoreRing({ score = 0, label = "WATER QUALITY", size = 200, color = "#51B0E6", statusWord = "", subline = "" }) {
  const R = (size - 24) / 2;
  const CX = size / 2;
  const CY = size / 2;
  const circ = 2 * Math.PI * R;
  const fill = (Math.min(100, Math.max(0, score)) / 100) * circ;

  return (
    <div style={{ position: "relative", width: size, height: size, margin: "0 auto" }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Track */}
        <circle cx={CX} cy={CY} r={R} fill="none" stroke="rgba(15, 20, 25, 0.05)" strokeWidth="9" />
        {/* Fill */}
        <circle
          cx={CX} cy={CY} r={R}
          fill="none"
          stroke={color}
          strokeWidth="9"
          strokeLinecap="round"
          strokeDasharray={`${fill} ${circ}`}
          transform={`rotate(-90 ${CX} ${CY})`}
          style={{ transition: "stroke-dasharray 1.5s cubic-bezier(0.34,1.56,0.64,1), stroke 0.8s ease" }}
        />
        {/* Soft glow layer */}
        <circle
          cx={CX} cy={CY} r={R}
          fill="none"
          stroke={`${color}22`}
          strokeWidth="14"
          strokeLinecap="round"
          strokeDasharray={`${fill} ${circ}`}
          transform={`rotate(-90 ${CX} ${CY})`}
          style={{ transition: "stroke-dasharray 1.5s cubic-bezier(0.34,1.56,0.64,1)", filter: "blur(8px)" }}
        />
      </svg>
      {/* Center: eyebrow, score in status color, status word, insight subline.
          All type scales from the ring size so proportions hold at any
          diameter. The subline is soft black, not brand blue: blue inside
          the ring read as a link and competed with the score. */}
      <div style={{
        position: "absolute", inset: 0,
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        pointerEvents: "none",
        padding: "0 19%",
        textAlign: "center",
      }}>
        <div style={{
          fontSize: Math.round(size * 0.05), fontWeight: 700, color: "#A6A8AB",
          letterSpacing: 2, textTransform: "uppercase", marginBottom: Math.round(size * 0.03),
        }}>
          {label}
        </div>
        <div style={{
          fontSize: Math.round(size * 0.25),
          fontWeight: 800,
          color: color,
          fontVariantNumeric: "tabular-nums",
          letterSpacing: -2,
          lineHeight: 1,
        }}>
          {score != null ? score : "\u2014"}
        </div>
        {statusWord && (
          <div style={{ fontSize: Math.round(size * 0.085), fontWeight: 700, color: color, marginTop: Math.round(size * 0.03), letterSpacing: 0.2 }}>
            {statusWord}
          </div>
        )}
        {subline && (
          <div style={{ fontSize: Math.round(size * 0.048), fontWeight: 600, color: "#3D4043", marginTop: Math.round(size * 0.02), lineHeight: 1.3, letterSpacing: 0.1 }}>
            {subline}
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// WTR INTELLIGENCE SYNOPSIS
// One actionable sentence per test result, the water-quality analog of
// "Drink 8 oz this hour to reach 93 tonight". The target scores quoted
// come from scoreFromTdsValue, so the promise always matches the model.
// ═══════════════════════════════════════════════════════════════

function wtrSynopsis(tds) {
  if (tds == null || tds <= 0) return null;
  const sc = scoreFromTdsValue(tds);
  const roTarget = scoreFromTdsValue(25);
  // projected: true when the line quotes a modeled future score (the RO
  // projection). The measurement badge is "verified" (hardware, founder
  // ruling 2026-07-11); a projected score is model output and carries its
  // own "developing" tier, per the tiers-on-all-intelligence-claims rule.
  if (sc >= 90) return {
    line: "Premium water. Balanced minerals and exceptional purity. This is the standard to hold every source to.",
    sub: "hold this standard",
    projected: false,
  };
  if (sc >= 80) return {
    line: `Good water with a light mineral load. A reverse osmosis pass would push this from ${sc} to ${roTarget}.`,
    sub: "premium within reach",
    projected: true,
  };
  if (sc >= 70) return {
    line: `Elevated dissolved solids. Carbon filters improve taste but not TDS; a reverse osmosis stage takes this from ${sc} to ${roTarget}.`,
    sub: "RO to reach premium",
    projected: true,
  };
  if (sc >= 60) return {
    line: `Heavy mineral load. Reverse osmosis takes this from ${sc} to ${roTarget}.`,
    sub: "RO strongly advised",
    projected: true,
  };
  return {
    line: "Very high dissolved solids. Treat with reverse osmosis before drinking this source regularly.",
    sub: "treat before drinking",
    projected: false,
  };
}

// ═══════════════════════════════════════════════════════════════
// METRIC CARD (v3.3 smoky mirror)
// ═══════════════════════════════════════════════════════════════

function MetricCard({ icon, label, value, unit, color = "#51B0E6", sublabel, hist = [] }) {
  const trend = hist.length >= 2 ? hist[hist.length - 1] - hist[hist.length - 2] : 0;
  const trendIcon = trend > 0.05 ? "\u2191" : trend < -0.05 ? "\u2193" : null;

  return (
    <div
      className="card-default"
      style={{
        padding: "18px 16px 14px",
        display: "flex",
        flexDirection: "column",
        gap: 0,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Subtle corner accent */}
      <div style={{
        position: "absolute", top: -20, right: -20,
        width: 60, height: 60, borderRadius: "50%",
        background: `${color}10`, pointerEvents: "none",
      }} />

      {/* Trend arrow: absolutely positioned so its appearance or
          disappearance can NEVER reflow the label row (the old inline
          arrow squeezed two-word labels onto two lines intermittently). */}
      {trendIcon && (
        <span style={{
          position: "absolute", top: 16, right: 14,
          fontSize: 13, color: trend > 0 ? "#C89B3C" : "#51B0E6", fontWeight: 700,
          pointerEvents: "none",
        }}>
          {trendIcon}
        </span>
      )}

      {/* Label */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
        <span style={{ fontSize: 15, lineHeight: 1 }}>{icon}</span>
        <span style={{ fontSize: 9.5, fontWeight: 700, color: "#A6A8AB", letterSpacing: 1, textTransform: "uppercase", whiteSpace: "nowrap" }}>
          {label}
        </span>
      </div>

      {/* Value */}
      <div style={{ display: "flex", alignItems: "baseline", gap: 4, marginBottom: 4 }}>
        <span style={{
          fontSize: 28, fontWeight: 800, color: value != null ? "#0F1419" : "#A6A8AB",
          fontVariantNumeric: "tabular-nums", letterSpacing: -1, lineHeight: 1,
          transition: "color 0.3s",
        }}>
          {value ?? "\u2014"}
        </span>
        {unit && value != null && (
          <span style={{ fontSize: 12, fontWeight: 600, color: "#A6A8AB" }}>{unit}</span>
        )}
      </div>

      {/* Sublabel */}
      {sublabel && (
        <div style={{
          fontSize: 10, fontWeight: 600, marginBottom: 4,
          color: sublabel.startsWith("\u26A0") ? "#C89B3C" : sublabel.startsWith("\u2713") ? "#4A8A6F" : "#A6A8AB",
        }}>
          {sublabel}
        </div>
      )}

      {/* Sparkline */}
      <div style={{ marginTop: "auto", paddingTop: 6 }}>
        <Sparkline data={hist} color={color} w={100} h={24} />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// WATER QUALITY TEST HISTORY
// Logs each completed water test (score + TDS + time) so the user can
// watch their water quality over time. This is the consumer replacement
// for the old dev History/Sensors/Debug tabs.
// SCALE-DEBT (named): persisted to localStorage, single device. The
// production answer is a backend test-events table keyed to user + bottle,
// so the log survives a device wipe and powers cross-device trends.
// ═══════════════════════════════════════════════════════════════

const TEST_HIST_KEY = "wtr_test_history";

function readTestHistory() {
  try { return JSON.parse(localStorage.getItem(TEST_HIST_KEY) || "[]"); } catch (e) { return []; }
}
function writeTestHistory(arr) {
  try { localStorage.setItem(TEST_HIST_KEY, JSON.stringify(arr.slice(-50))); } catch (e) {}
}

function relTime(ts) {
  const m = Math.floor((Date.now() - ts) / 60000);
  if (m < 1) return "Just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(ts).toLocaleDateString();
}

function TestHistoryCard({ history = [] }) {
  if (!history.length) {
    return (
      <div className="card-default" style={{ padding: "20px 16px", marginBottom: 14 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: "#A6A8AB", letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 10 }}>
          Water Quality History
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0" }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, background: "rgba(81,176,230,0.10)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <BtlIcon name="flask" size={18} color="#51B0E6" />
          </div>
          <div style={{ fontSize: 13, color: "#3D4043", lineHeight: 1.5 }}>
            Run your first water test to start tracking your water quality over time.
          </div>
        </div>
      </div>
    );
  }
  const recent = [...history].reverse().slice(0, 10);
  // Re-score stored entries from their saved TDS at render time, so history
  // written under any older scoring model displays under the current one.
  const scores = history.slice(-12).map(h => scoreFromTdsValue(h.tds));
  return (
    <div className="card-default" style={{ padding: "18px 16px", marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#A6A8AB", letterSpacing: 1.5, textTransform: "uppercase" }}>
            Water Quality History
          </div>
          <div style={{ fontSize: 12, color: "#3D4043", marginTop: 2 }}>
            Your last {recent.length} test{recent.length > 1 ? "s" : ""}
          </div>
        </div>
        <Sparkline data={scores} color="#51B0E6" w={90} h={28} />
      </div>
      <div style={{ display: "flex", flexDirection: "column" }}>
        {recent.map((h, i) => {
          const sc = scoreFromTdsValue(h.tds);
          const bc = bandColor(sc);
          const c = bc;
          return (
            <div key={h.ts} style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "11px 0",
              borderBottom: i < recent.length - 1 ? "1px solid rgba(15,20,25,0.06)" : "none",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: 12, background: `${bc}14`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <span style={{ fontSize: 15, fontWeight: 800, color: bc, fontVariantNumeric: "tabular-nums" }}>{sc}</span>
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#0F1419" }}>{scoreBandWord(sc)}</div>
                  <div style={{ fontSize: 11, color: "#A6A8AB" }}>{relTime(h.ts)}{h.source ? ` \u00B7 ${h.source}` : ""}</div>
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <span style={{ fontSize: 16, fontWeight: 800, color: c, fontVariantNumeric: "tabular-nums" }}>{h.tds}</span>
                <span style={{ fontSize: 11, color: "#A6A8AB", marginLeft: 3 }}>ppm</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════

export default function WTRBottleScreen({ onMenu, onOpenBottleScan }) {
  // -- Live connection comes from the module-level BLE singleton, which lives
  //    OUTSIDE React and survives tab switches / unmounts. This screen no
  //    longer OWNS the GATT link; it subscribes. Navigating away and back no
  //    longer drops the bottle. A full page reload still needs one tap: no
  //    Web Bluetooth link survives a reload.
  const conn   = useBottleConnection();
  const ble    = conn.status;                 // idle|scanning|connected|error
  const errMsg = conn.errMsg;
  const log    = conn.log;

  // Demo telemetry stays component-local so fake frames can never share a
  // buffer with real ones. Real frames come from the singleton (conn.*).
  const [demoTele,   setDemoTele]   = useState(null);
  const [demoHist,   setDemoHist]   = useState([]);
  const [demoIntake, setDemoIntake] = useState(0);

  const [goalML,       setGoalML]       = useState(2000);
  const [goalInput,    setGoalInput]    = useState("2000");
  const [showGoal,     setShowGoal]     = useState(false);
  const [tab,          setTab]          = useState("live");
  const [testPhase,    setTestPhase]    = useState(null); // null | "tds" | "vol" | "temp" | "done"
  const [testHasRun,   setTestHasRun]   = useState(false); // true after first test completes
  // Unified daily hydration: verified backend total + the NHS predicted target.
  // Unified daily figures, all from the SAME summary endpoint My Hydration uses.
  const [dailyMl,        setDailyMl]        = useState(null); // absorbed (manual + verified)
  const [dailyTargetMl,  setDailyTargetMl]  = useState(2000);
  const [dailyVerifiedMl,setDailyVerifiedMl]= useState(0);
  const [dailyLive,      setDailyLive]      = useState(false);
  const [demoActive,   setDemoActive]   = useState(false);
  const [testHistory,  setTestHistory]  = useState(() => readTestHistory());
  // Pinned display result for the score ring, TDS stat, and contaminant
  // alert. Seeded from the stored test history so a returning user sees
  // their last result immediately. Updated instantly on test completion
  // and refreshed from live telemetry on a 20 s cycle (median-stabilized).
  const [pinnedTest,   setPinnedTest]   = useState(() => {
    const th = readTestHistory();
    return th.length
      ? { tds: th[th.length - 1].tds, score: scoreFromTdsValue(th[th.length - 1].tds), ts: th[th.length - 1].ts }
      : null;
  });
  // Pinned bottle level: the Level figure the user sees. Updated only when
  // a median-smoothed reading moves LEVEL_EVENT_ML or more (a sip or fill),
  // never by ToF jitter on a still bottle.
  const [pinnedLevelMl, setPinnedLevelMl] = useState(null);
  // The ts of the most recent test still awaiting a source tag, if any.
  const [sourcePromptTs, setSourcePromptTs] = useState(null);
  // Compare My Waters overlay (personal source scoreboard).
  const [showCompare, setShowCompare] = useState(false);
  // Sensor debug panel (dev branch): visible only when truly connected.
  const [showDebug,    setShowDebug]    = useState(true);
  const [dbgCopied,    setDbgCopied]    = useState(false);

  const demoRef  = useRef(null);
  const testCapturedRef = useRef(false); // guards against double-logging one test
  const histRef  = useRef([]);           // latest telemetry buffer for the 20 s display sampler
  const teleRef  = useRef(null);         // latest frame for callbacks that must not re-create per frame
  // Submersion gate feedback: set when RUN WATER TEST is refused for
  // insufficient water, cleared automatically. Never blocks the UI.
  const [testGateMsg, setTestGateMsg] = useState(null);
  const gateMsgTimerRef = useRef(null);

  // -- Render source: demo overlay when demo is active, else the live
  //    singleton snapshot. Every downstream derived value and effect reads
  //    these, so the whole UI below is untouched by the refactor.
  const remembered = conn.remembered;
  const tele   = demoActive ? demoTele   : conn.tele;
  const hist   = demoActive ? demoHist   : conn.hist;
  const intake = demoActive ? demoIntake : conn.intake;

  // Connection actions delegate to the singleton (names preserved so the
  // existing hero buttons need no changes).
  const connect    = conn.connect;
  const reconnect  = conn.reconnect;
  const disconnect = conn.disconnect;

  const resetIntake = useCallback(async () => {
    if (demoActive) { setDemoIntake(0); return; }
    await conn.resetIntake();
  }, [demoActive, conn]);

  const applyGoal = useCallback(async () => {
    const ml = parseInt(goalInput, 10);
    if (isNaN(ml) || ml < 250 || ml > 5000) return;
    setGoalML(ml);
    await conn.setGoal(ml);
    setShowGoal(false);
  }, [goalInput, conn]);

  // Unified daily figures from the SAME summary endpoint My Hydration uses, so
  // the two tabs read one source. absorbedL is the headline (manual + verified),
  // matching My Hydration exactly; verifiedL is the bottle-only portion, the ONLY
  // number the "Live from your bottle" chip is allowed to claim.
  useEffect(() => {
    const email = ((readProfile().email) || "").trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await fetch(`${API_BASE}/api/wtr/hydration/summary?units=l&email=${encodeURIComponent(email)}`, {
          headers: authHeaders(),
        });
        if (!r.ok) return;
        const j = await r.json();
        if (!j.ok || !j.intake) return;
        if (!cancelled) {
          setDailyMl(Math.round((j.intake.absorbedL || 0) * 1000));
          setDailyTargetMl(Math.round((j.intake.targetL || 0) * 1000) || 2000);
          setDailyVerifiedMl(Math.round((j.intake.verifiedL || 0) * 1000));
          setDailyLive((j.intake.verifiedL || 0) > 0);
        }
      } catch (e) {}
    };
    poll();
    const t = setInterval(poll, TODAY_POLL_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // Verified Intake Event sync now lives in lib/bleManager, driven per real
  // frame at the transport level. It is deliberately NOT a screen effect
  // anymore: episode detection keeps running while the user is on any tab,
  // not only while this screen is mounted. Demo frames never reach the
  // manager, and bottleSync rejects demo device ids internally regardless.

  // ── Demo mode ─────────────────────────────────────────────────
  const startDemo = useCallback(() => {
    setDemoActive(true);
    const demoTick = () => {
      const baseTds = 24 + Math.floor(Math.random() * 12);
      const basePh  = 720 + Math.floor(Math.random() * 40);
      const baseTemp = 195 + Math.floor(Math.random() * 30);
      const fakeInfo = {
        state: 1,
        charging: false,
        batt: 82 + Math.floor(Math.random() * 10),
        fw: 501,
        sensors: {
          tds: { label: "TDS", val: baseTds, unit: "ppm", raw: baseTds, type: 2, status: 0 },
          ph: { label: "pH", val: (basePh / 100).toFixed(2), unit: "pH", raw: basePh, type: 3, status: 0 },
          waterTemp: { label: "Water Temp", val: (baseTemp / 10).toFixed(1), unit: "°C", raw: baseTemp, type: 5, status: 0 },
          waterLevel: { label: "Water Level", val: 180 + Math.floor(Math.random() * 40), unit: "mm", raw: 180, type: 4, status: 0 },
          volume: { label: "Volume", val: 400 + Math.floor(Math.random() * 200), unit: "mL", raw: 400 + Math.floor(Math.random() * 200), type: 6, status: 0 },
        },
        ts: Date.now(),
      };
      setDemoTele(fakeInfo);
      setDemoHist(p => [...p.slice(-(MAX_HIST - 1)), fakeInfo]);
      setDemoIntake(p => Math.min(p + Math.floor(Math.random() * 40) + 10, 2500));
    };
    demoTick();
    demoRef.current = setInterval(demoTick, POLL_MS);
  }, []);

  const stopDemo = useCallback(() => {
    setDemoActive(false);
    clearInterval(demoRef.current);
    setDemoTele(null); setDemoHist([]); setDemoIntake(0);
  }, []);

  useEffect(() => () => clearInterval(demoRef.current), []);

  // If a real connection comes up while a demo is running, drop the demo so
  // fake and real telemetry never interleave. connect() no longer lives in
  // this component, so this guard replaces the old in-connect() demo kill.
  useEffect(() => {
    if (ble === "connected" && demoActive) stopDemo();
  }, [ble, demoActive, stopDemo]);

  // ── Water test ─────────────────────────────────────────────────
  // VALIDITY PRECONDITION (2026-07-11): the test does not execute below
  // MIN_TEST_LEVEL_ML of settled water. An unsubmerged TDS electrode reads
  // the last stamped value or residual film, and any score built on it is
  // fiction. Below the gate: no phases, no score, no narrative, no tier.
  // Demo mode passes through the same gate on its own (demo frames carry
  // a real above-gate level), so demos still look complete.
  const runTest = useCallback(() => {
    const lvl = settledLevelMl(histRef.current, teleRef.current);
    if (lvl == null || lvl < MIN_TEST_LEVEL_ML) {
      setTestGateMsg(
        lvl == null
          ? "Connect your bottle to run a water test."
          : `Not enough water to run a test. Fill to at least ${Math.round(MIN_TEST_LEVEL_ML / 29.5735)} oz and try again.`
      );
      clearTimeout(gateMsgTimerRef.current);
      gateMsgTimerRef.current = setTimeout(() => setTestGateMsg(null), 6000);
      return;
    }
    setTestGateMsg(null);
    setTestPhase("tds");
    setTimeout(() => setTestPhase("vol"), 1500);
    setTimeout(() => setTestPhase("temp"), 3000);
    setTimeout(() => { setTestPhase("done"); setTestHasRun(true); }, 4500);
    setTimeout(() => setTestPhase(null), 8000);
  }, []);
  useEffect(() => () => clearTimeout(gateMsgTimerRef.current), []);

  // ── Log the completed test to Water Quality History (once per test) ─────────
  useEffect(() => {
    if (testPhase === null) { testCapturedRef.current = false; return; }
    if (testPhase !== "done" || testCapturedRef.current) return;
    const sx = tele?.sensors ?? {};
    const tdsNow = sx.tds?.raw ?? null;
    // Stabilized capture: median of the recent positive TDS frames in the
    // poll buffer (plus the current frame), so one noisy or near-dry frame
    // cannot become the official test result. Median of real reads only.
    const recentTds = hist.slice(-6).map(h => h.sensors?.tds?.raw).concat([tdsNow]);
    const tds = medianOfPositive(recentTds);
    if (tds == null || tds <= 0) return; // only log a real reading
    // Defense in depth (2026-07-11): re-verify submersion at capture time.
    // The gate on runTest covers the button; this covers the 4.5 s between
    // press and capture (a drain during the phases must void the test).
    // A positive median TDS with a below-gate level is exactly the stale
    // electrode signature and is never a loggable result.
    const capLvl = settledLevelMl(hist, tele);
    if (capLvl == null || capLvl < MIN_TEST_LEVEL_ML) return;
    testCapturedRef.current = true;
    const entry = {
      ts: Date.now(),
      score: scoreFromTdsValue(tds),
      tds,
      levelMl: Math.round(capLvl), // submersion evidence, carried to the API gate
      source: null,
      ph: sx.ph?.val ?? null,
      // Temp intentionally NOT recorded while fw=10 transmits whole degrees
      // against the deci-degree spec (defect 1): a stored wrong reading would
      // outlive the firmware fix. Re-enable after Production Partners ship it.
      tempF: null,
    };
    setTestHistory(prev => {
      const next = [...prev, entry].slice(-50);
      writeTestHistory(next);
      return next;
    });
    // A completed test updates the pinned display immediately: the user
    // acted, the result should not wait for the next 20 s sample tick.
    setPinnedTest({ tds, score: entry.score, ts: entry.ts });
    // And opens the source tag prompt for this test.
    setSourcePromptTs(entry.ts);
  }, [testPhase, tele, hist]);

  // Keep the sampler's view of the telemetry buffer current without
  // recreating the interval on every frame.
  useEffect(() => { histRef.current = hist; }, [hist]);
  useEffect(() => { teleRef.current = tele; }, [tele]);

  // ── Level event gate ──────────────────────────────────────────
  // Candidate level is the median of the last few frames (slosh and
  // dropout resistant). The pinned figure updates only when the candidate
  // moves LEVEL_EVENT_ML or more from what is displayed: a sip or a fill.
  useEffect(() => {
    if (!tele) return;
    const cand = medianOfPositive(
      histRef.current.slice(-3).map(h => levelToMl(h.sensors?.waterLevel?.val))
        .concat([levelToMl(tele.sensors?.waterLevel?.val)])
    );
    if (cand == null) return;
    setPinnedLevelMl(prev =>
      prev == null || Math.abs(cand - prev) >= LEVEL_EVENT_ML ? cand : prev
    );
  }, [tele]);

  // ── Derived values ────────────────────────────────────────────
  const s          = tele?.sensors ?? {};
  const tdsRaw     = s.tds?.raw    ?? null;
  const dayMl      = dailyMl != null ? dailyMl : 0;
  const goalDayMl  = dailyTargetMl;
  const score      = tele ? waterScore(s) : null;

  // ── Pinned display result ─────────────────────────────────────
  // The score ring, TDS stat, and contaminant alert render pinnedTest, not
  // live per-frame telemetry. Live electrode noise (2026-07-08 recording:
  // raw TDS swinging within seconds on a still bottle) made the headline
  // flip 88 to 95 and back every few polls. pinnedTest updates on test
  // completion and on a 20 s sample cycle, always median-stabilized.
  // Live telemetry still drives IN BOTTLE, sparklines, and the debug panel.
  const displayTds = pinnedTest ? pinnedTest.tds : null;
  const displayScore = pinnedTest ? pinnedTest.score : null;
  // A returning user with stored history sees their last result on load,
  // no new test required.
  const hasResult = testHasRun || testHistory.length > 0;
  const lastTest = testHistory.length ? testHistory[testHistory.length - 1] : null;

  const col        = bandColor(displayScore);
  const histOf     = key =>
    hist.map(h => { const v = h.sensors?.[key]?.val; return v != null ? parseFloat(v) : null; })
        .filter(v => v != null);

  const volVal    = s.volume?.val    ?? null;  // firmware 0x06: DEFECTIVE, debug panel only
  const tempVal  = s.waterTemp?.val ?? null;
  const levelVal = s.waterLevel?.val?? null;
  const battVal  = tele?.batt       ?? null;

  const isLive = ble === "connected" || tele !== null;
  const isDemo = demoActive && ble !== "connected";

  // ── Source tagging ────────────────────────────────────────────
  const tagSource = useCallback((ts, label, brand = null, attributionMode = "manual") => {
    let tagged = null;
    setTestHistory(prev => {
      const next = prev.map(h => {
        if (h.ts === ts) { tagged = { ...h, source: label, ...(brand ? { brand } : {}) }; return tagged; }
        return h;
      });
      writeTestHistory(next);
      return next;
    });
    // Note: the prompt is NOT closed here anymore. SourceTaggingCard owns
    // its own done phase (insight line plus Trust But Verify bridge); its
    // onClose clears sourcePromptTs when the user is finished.
    // Fire-and-forget training post. Failure never blocks the UI; the
    // label is already saved locally either way.
    try {
      const email = ((readProfile().email) || "").trim().toLowerCase();
      if (email && tagged) {
        fetch(`${API_BASE}/api/bottle/test-source`, {
          method: "POST",
          headers: { ...authHeaders(), "Content-Type": "application/json" },
          body: JSON.stringify({
            email,
            testedAt: new Date(ts).toISOString(),
            source: label,
            brand: brand || null,
            attributionMode,
            tds: tagged.tds,
            score: tagged.score,
          }),
        }).catch((e) => console.error("test-source post failed:", e && e.message));
      }
    } catch (e) {}
  }, []);

  // ── 20 s display sampler ──────────────────────────────────────
  // While telemetry is flowing, refresh the pinned result every 20 s from
  // the median of the recent positive TDS frames. Dynamic, but on a human
  // cadence, and one noisy frame can never become the displayed number.
  // Client-side only: this adds zero API traffic, and the Absorption
  // Intelligence card still refetches once per completed test.
  useEffect(() => {
    if (!isLive) return;
    const tick = () => {
      // Submersion gate (2026-07-11): below MIN_TEST_LEVEL_ML the electrode
      // is reporting film or a stale stamp, and medianOfPositive would
      // launder it (the near-zero honest frames are exactly the ones the
      // positive filter discards). Do not refresh the pinned result; the
      // last valid test stands, with its own timestamp.
      const lvl = settledLevelMl(histRef.current, teleRef.current);
      if (lvl == null || lvl < MIN_TEST_LEVEL_ML) return;
      const recent = histRef.current.slice(-6).map(h => h.sensors?.tds?.raw);
      const m = medianOfPositive(recent);
      if (m != null && m > 0) {
        setPinnedTest({ tds: m, score: scoreFromTdsValue(m), ts: Date.now() });
      }
    };
    const t = setInterval(tick, DISPLAY_SAMPLE_MS);
    return () => clearInterval(t);
  }, [isLive]);

  // App-computed bottle contents from the ToF level: the ONLY volume figure
  // rendered to the user. Dropout frames (level 0) render as a dash, never a
  // number. The DISPLAYED figure is pinnedLevelMl (event-gated); the live
  // per-frame value feeds only the sparkline and the event gate itself.
  const levelDisplayMl = pinnedLevelMl;
  const inBottleHist = hist
    .map(h => levelToMl(h.sensors?.waterLevel?.val))
    .filter(v => v != null);
  const inBottleColor = () => {
    if (levelDisplayMl == null) return "#51B0E6";
    if (levelDisplayMl < 100) return "#C89B3C";
    return "#4A8A6F";
  };

  // WTR Intelligence synopsis for the pinned result.
  const wtrSyn = displayTds != null && displayTds > 0 ? wtrSynopsis(displayTds) : null;

  // Live submersion state (2026-07-11): true only when telemetry is flowing
  // AND the settled level clears the gate. Drives the LIVE TDS card and the
  // fresh-test affordances. The ScoreRing renders the last valid TEST (a
  // historical fact with its own timestamp) and is not gated by this.
  const liveSettledMl = isLive ? settledLevelMl(hist, tele) : null;
  const submerged = liveSettledMl != null && liveSettledMl >= MIN_TEST_LEVEL_ML;

  // Human timestamp for the pinned test: time today, date otherwise. The
  // result must never read as a live claim; it is a record of when the
  // water it describes was actually measured.
  const testedAtLabel = (() => {
    if (!pinnedTest || !pinnedTest.ts) return null;
    const d = new Date(pinnedTest.ts);
    const sameDay = new Date().toDateString() === d.toDateString();
    return sameDay
      ? `Tested ${d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`
      : `Tested ${d.toLocaleDateString([], { month: "short", day: "numeric" })}`;
  })();

  // Temp honesty: fw=10 transmits whole degrees C against the deci-degree spec
  // (defect 1), so the decoded value is wrong 10x on real hardware. Until
  // Production Partners ship the fix, real-hardware temp renders as
  // "Calibrating". Demo frames are correctly scaled and may render, so demos
  // still look complete. Do NOT hand-patch a 10x correction here: it would
  // silently break the day compliant firmware ships.
  const tempC  = tempVal != null ? parseFloat(tempVal) : null;
  const tempF  = (isDemo && tempC != null && tempC !== 0) ? (tempC * 9/5 + 32).toFixed(0) : null;

  // ── Absorption Intelligence live reading ──────────────────────
  // Keyed to the LAST COMPLETED TEST (lastTest above), not raw telemetry:
  // tele updates every 2 s poll frame, and using it directly would refetch
  // the Absorption Intelligence endpoint on every frame. testHistory
  // advances exactly once per completed test, so the card refreshes when a
  // test lands and only then. tempF is passed from the logged entry (null
  // while fw=10 misreports temp; the card and route both tolerate null).
  const absorptionLive = useMemo(
    () =>
      lastTest
        ? {
            tdsPpm: lastTest.tds,
            qualityScore: scoreFromTdsValue(lastTest.tds),
            tempF: lastTest.tempF,
            // Submersion evidence for the server-side gate. Entries logged
            // before 2026-07-11 have no levelMl; the card omits the param
            // and the server then ignores the live values (correct: an
            // unevidenced live reading must not override stored history).
            levelMl: lastTest.levelMl != null ? lastTest.levelMl : null,
          }
        : null,
    [lastTest ? lastTest.ts : null]
  );

  // ── Sensor debug derived values (dev branch) ──────────────────
  const dbgStats = useMemo(
    () => (ble === "connected" ? sensorStatsFromHist(hist) : {}),
    [ble, hist]
  );
  const dbgSeenTypes = useMemo(
    () => new Set(Object.values(dbgStats).map(x => x.type)),
    [dbgStats]
  );
  const dbgMissing = EXPECTED_SENSORS.filter(e => hist.length > 0 && !dbgSeenTypes.has(e.type));
  const dbgReport = useMemo(
    () => (ble === "connected" && showDebug ? buildDebugReport({ tele, hist, log }) : ""),
    [ble, showDebug, tele, hist, log]
  );
  const copyDebugReport = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(buildDebugReport({ tele, hist, log }));
      setDbgCopied(true);
      setTimeout(() => setDbgCopied(false), 2000);
    } catch (e) {
      // Clipboard blocked (possible in Bluefy). The textarea below is selectable
      // by hand, so nothing is lost.
      setDbgCopied(false);
    }
  }, [tele, hist, log]);

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════

  return (
    <div data-testid="wtr-btl-screen" style={{
      fontFamily:              "Montserrat, -apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif",
      // v3.3 substrate: white + radial brand-blue glow (matches body for cohesion when parent doesn't bleed through)
      background:              "#FFFFFF",
      backgroundImage:         "radial-gradient(ellipse 80% 60% at 80% 100%, rgba(81, 176, 230, 0.10), transparent 60%), radial-gradient(ellipse 60% 50% at 10% 0%, rgba(81, 176, 230, 0.05), transparent 60%)",
      backgroundAttachment:    "fixed",
      minHeight:               "100%",
      color:                   "#0F1419",
      display:                 "flex",
      flexDirection:           "column",
      maxWidth:                480,
      margin:                  "0 auto",
      WebkitFontSmoothing:     "antialiased",
      MozOsxFontSmoothing:     "grayscale",
    }}>

      {/* ── Animations ── */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse {
          0%, 100% { box-shadow: 0 4px 20px rgba(81,176,230,0.3); }
          50%      { box-shadow: 0 4px 30px rgba(81,176,230,0.6); }
        }
        @keyframes shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        @keyframes ringPulse {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50%       { opacity: 0.1; transform: scale(1.08); }
        }
        @keyframes blinkDot {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.3; }
        }
        * { box-sizing: border-box; -webkit-tap-highlight-color: transparent; }
        ::-webkit-scrollbar { width: 3px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(15, 20, 25, 0.10); border-radius: 2px; }
      `}</style>

      {/* ═══════════════════════════════════════════════════════════
          SECTION 1: HERO VISUAL (UNCHANGED per spec - keep brand moment)
          ═══════════════════════════════════════════════════════════ */}
      <div style={{
        position: "relative",
        width: "100%",
        height: isLive ? 220 : 340,
        overflow: "hidden",
        transition: "height 0.5s ease",
      }}>
        {/* Background image */}
        <img
          src="/bottle-cap-sensor-glow.jpg"
          alt=""
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />
        {/* Gradient overlay to white substrate */}
        <div style={{
          position: "absolute", inset: 0,
          background: "linear-gradient(180deg, rgba(255,255,255,0) 30%, rgba(255,255,255,0.7) 70%, #FFFFFF 100%)",
        }} />
        {/* Dark overlay for text readability */}
        <div style={{
          position: "absolute", inset: 0,
          background: "linear-gradient(180deg, rgba(15,20,25,0.5) 0%, rgba(15,20,25,0.1) 50%, transparent 100%)",
        }} />

        {/* Title overlay: unified AppHeader (hero variant) */}
        <AppHeader
          variant="hero"
          eyebrow="Intelligent WTR Bottle"
          title="WTR BTL"
          onMenu={onMenu}
          pill={{
            label: isDemo ? "DEMO MODE" : ble === "connected" ? (conn.deviceName?.slice(0,10) || "CONNECTED") : ble === "scanning" ? "SCANNING" : "OFFLINE",
            tone: isDemo ? "neutral" : ble === "connected" ? "live" : "offline",
          }}
        />

        {/* Connect / Disconnect button overlay (bottom of hero) */}
        <div style={{
          position: "absolute", bottom: 16, left: 20, right: 20,
          display: "flex", gap: 8, zIndex: 2,
        }}>
          {ble === "connected" ? (
            <button onClick={disconnect} data-testid="disconnect-btn" style={{
              flex: 1, padding: "12px 0", background: "#FFFFFF", color: "#B84A4A",
              border: "1px solid rgba(184, 74, 74, 0.18)", borderRadius: 14, fontSize: 13, fontWeight: 700,
              cursor: "pointer", boxShadow: "0 2px 8px rgba(15, 20, 25, 0.08)",
            }}>
              Disconnect
            </button>
          ) : (
            <>
              <button onClick={remembered ? reconnect : connect} disabled={ble === "scanning"} data-testid="connect-wtr-btl-btn" className="btn-brand" style={{
                flex: 1, padding: "12px 0",
                background: ble === "scanning" ? "rgba(15, 20, 25, 0.06)" : "#51B0E6",
                color: ble === "scanning" ? "#A6A8AB" : "#FFFFFF",
                border: "none", borderRadius: 14, fontSize: 13, fontWeight: 700,
                cursor: ble === "scanning" ? "not-allowed" : "pointer",
                boxShadow: ble === "scanning" ? "none" : "0 4px 16px rgba(81,176,230,0.3)",
              }}>
                {ble === "scanning" ? "Scanning\u2026" : remembered ? "Reconnect Bottle" : "Connect Device"}
              </button>
              {!isLive && (
                <button onClick={demoActive ? stopDemo : startDemo} style={{
                  padding: "12px 20px",
                  background: demoActive ? "rgba(200, 155, 60, 0.10)" : "#FFFFFF",
                  color: demoActive ? "#C89B3C" : "#0F1419",
                  border: demoActive ? "1px solid rgba(200, 155, 60, 0.25)" : "1px solid rgba(15, 20, 25, 0.06)",
                  borderRadius: 14, fontSize: 13, fontWeight: 700,
                  cursor: "pointer", boxShadow: "0 2px 8px rgba(15, 20, 25, 0.08)",
                }}>
                  {demoActive ? "Stop Demo" : "Demo"}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Error message */}
      {errMsg && (
        <div data-testid="ble-error-msg" className="card-critical" style={{
          margin: "0 16px 8px", padding: "12px 16px", fontSize: 13, color: "#B84A4A", lineHeight: 1.5,
        }}>
          <BtlIcon name="hazard" size={13} color="#B84A4A" /> {errMsg}
        </div>
      )}

      {/* Failure-time connection log (2026-07-15). The transport logs every
          handshake step with labeled timeouts, but the Sensor Debug panel
          only mounts when connected, so the one state that needed the log
          hid it and every failed connect was debugged blind. Renders only
          alongside an error, newest first, and disappears the moment a
          connection succeeds. Read-only diagnostics: no fabricated values,
          nothing here is a result. */}
      {errMsg && ble !== "connected" && log.length > 0 && (
        <div className="card-default" style={{ margin: "0 16px 8px", padding: "12px 16px" }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#A6A8AB", letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 8 }}>
            Connection Log (newest first)
          </div>
          <div style={{ maxHeight: 180, overflowY: "auto", fontFamily: "Menlo, 'SF Mono', Consolas, monospace" }}>
            {log.slice(0, 15).map((e, i) => (
              <div key={i} style={{
                fontSize: 9, padding: "3px 0", lineHeight: 1.5,
                borderBottom: "1px solid rgba(15,20,25,0.04)",
                color: e.msg.startsWith("\u2717") ? "#B84A4A" : "#3D4043",
                fontWeight: e.msg.startsWith("\u2717") ? 700 : 400,
                wordBreak: "break-all",
              }}>
                [{e.t}] {e.msg}{e.hex ? " " + e.hex.slice(0, 47) : ""}
              </div>
            ))}
          </div>
          <div style={{ fontSize: 9, color: "#A6A8AB", marginTop: 6, lineHeight: 1.5 }}>
            Screenshot this if you report a connection problem.
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════
          CONNECT SCREEN (when idle and no data)
          ═══════════════════════════════════════════════════════════ */}
      {!isLive && ble !== "scanning" && (
        <div style={{
          padding: "0 20px 40px",
          display: "flex", flexDirection: "column", alignItems: "center",
          textAlign: "center", gap: 24,
          animation: "fadeInUp 0.4s ease",
        }}>
          {/* Product lineup */}
          <div style={{
            width: "100%", borderRadius: 20, overflow: "hidden",
            boxShadow: "0 4px 20px rgba(15, 20, 25, 0.08)",
            border: "1px solid rgba(255, 255, 255, 0.55)",
          }}>
            <img src="/bottle-lineup-5colors.jpg" alt="Generosity WTR BTL lineup"
              style={{ width: "100%", height: "auto", objectFit: "contain", display: "block", maxHeight: 300 }} />
          </div>

          <div style={{ fontSize: 14, color: "#3D4043", lineHeight: 1.7, maxWidth: 300 }}>
            Connect your Generosity{"\u2122"} Smart Cap to monitor live water quality, track daily intake, and receive real-time TDS readings.
          </div>

          {/* Features grid */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, width: "100%" }}>
            {[
              { icon: <BtlIcon name="droplet" size={20} color="#51B0E6" />, label: "TDS\nMonitoring" },
              { icon: <BtlIcon name="ruler" size={20} color="#4A8A6F" />, label: "Intake\nTracking" },
              { icon: <BtlIcon name="thermometer" size={20} color="#C89B3C" />, label: "Temp\nSensor" },
            ].map((f, i) => (
              <div key={i} className="card-default" style={{
                padding: "18px 8px",
                textAlign: "center",
              }}>
                <div style={{ marginBottom: 8 }}>{f.icon}</div>
                <div style={{ fontSize: 11, fontWeight: 600, color: "#3D4043", lineHeight: 1.4, whiteSpace: "pre-line" }}>
                  {f.label}
                </div>
              </div>
            ))}
          </div>

          <div style={{ fontSize: 11, color: "#A6A8AB", maxWidth: 280, lineHeight: 1.7 }}>
            Requires Bluefy on iPhone or iPad, or Chrome on Android or desktop, with Bluetooth enabled.
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════
          SCANNING OVERLAY
          ═══════════════════════════════════════════════════════════ */}
      {ble === "scanning" && (
        <div style={{
          padding: "40px 20px", display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center", gap: 28, textAlign: "center",
        }}>
          <div style={{ position: "relative", width: 80, height: 80 }}>
            {[
              { sz: 80, dur: "1.2s", sw: 2.5, color: "#51B0E6" },
              { sz: 56, dur: "1.8s", sw: 1.5, color: "#51B0E680" },
            ].map(({ sz, dur, sw, color }, i) => (
              <div key={i} style={{
                position: "absolute", inset: (80 - sz) / 2,
                borderRadius: "50%", border: `${sw}px solid transparent`,
                borderTop: `${sw}px solid ${color}`,
                animation: `spin ${dur} linear ${i % 2 === 0 ? "normal" : "reverse"} infinite`,
              }} />
            ))}
            <div style={{
              position: "absolute", inset: 0, display: "flex",
              alignItems: "center", justifyContent: "center",
            }}>
              <BtlIcon name="signal" size={22} color="#51B0E6" />
            </div>
          </div>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#0F1419", marginBottom: 8 }}>
              Searching for Smart Cap
            </div>
            <div style={{ fontSize: 13, color: "#A6A8AB", lineHeight: 1.6 }}>
              Make sure Bluetooth is on and the lid is powered up
            </div>
          </div>
          <button onClick={() => conn.cancelScan()} data-testid="cancel-scan-btn" style={{
            background: "rgba(255, 255, 255, 0.55)",
            backdropFilter: "blur(50px) saturate(180%)",
            WebkitBackdropFilter: "blur(50px) saturate(180%)",
            border: "1px solid rgba(15, 20, 25, 0.06)", borderRadius: 14,
            color: "#3D4043", padding: "12px 32px", fontSize: 13, fontWeight: 600, cursor: "pointer",
            boxShadow: "0 1px 4px rgba(15, 20, 25, 0.04)",
          }}>
            Cancel
          </button>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════
          LIVE DASHBOARD
          ═══════════════════════════════════════════════════════════ */}
      {isLive && (
        <div style={{ padding: "0 16px 100px", animation: "fadeInUp 0.3s ease" }}>

          {/* ── SECTION 2: Score Ring ── */}
          <div className="card-default" style={{
            padding: "28px 20px 24px",
            marginBottom: 14,
            textAlign: "center",
          }}>
            {/* Testing sequence runs INSIDE this card, then converts to the
                score ring when the result lands. The ring renders the pinned
                result: eyebrow, status-colored score, band word, and a blue
                intelligence subline, with the WTR Intelligence synopsis panel
                beneath. Live noise never reaches any of it. */}
            {testPhase != null && testPhase !== "done" ? (
              <div style={{ textAlign: "left" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#A6A8AB", letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 16 }}>
                  TESTING IN PROGRESS
                </div>
                {["tds", "vol", "temp"].map((phase, i) => {
                  const labels = { tds: "Analyzing TDS...", vol: "Reading water level...", temp: "Measuring temperature..." };
                  const icons = { tds: <BtlIcon name="droplet" size={16} color="#51B0E6" />, vol: <BtlIcon name="ruler" size={16} color="#4A8A6F" />, temp: <BtlIcon name="thermometer" size={16} color="#C89B3C" /> };
                  const isActive = testPhase === phase;
                  const isDone = (phase === "tds" && (testPhase === "vol" || testPhase === "temp")) || (phase === "vol" && testPhase === "temp");
                  return (
                    <div key={phase} style={{
                      display: "flex", alignItems: "center", gap: 12,
                      padding: "10px 0",
                      borderBottom: i < 2 ? "1px solid rgba(15, 20, 25, 0.06)" : "none",
                      opacity: isDone || isActive ? 1 : 0.4,
                    }}>
                      <div style={{
                        width: 32, height: 32, borderRadius: 10,
                        background: isDone ? "rgba(74, 138, 111, 0.10)" : isActive ? "rgba(81, 176, 230, 0.10)" : "rgba(15, 20, 25, 0.04)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        {isDone ? (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4A8A6F" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                        ) : isActive ? (
                          <div style={{ width: 16, height: 16, borderRadius: "50%", border: "2px solid transparent", borderTop: "2px solid #51B0E6", animation: "spin 0.8s linear infinite" }} />
                        ) : icons[phase]}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: isDone ? "#0F1419" : "#A6A8AB" }}>
                          {labels[phase]}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : hasResult && displayTds != null && displayTds > 0 ? (
              <>
                <ScoreRing
                  score={displayScore}
                  label="WATER QUALITY"
                  size={210}
                  color={col}
                  statusWord={scoreBandWord(displayScore)}
                  subline={wtrSyn ? wtrSyn.sub : ""}
                />
                {wtrSyn && (
                  <div style={{
                    background: "rgba(15, 20, 25, 0.04)",
                    borderRadius: 16,
                    padding: "16px 18px",
                    margin: "16px 0 2px",
                    textAlign: "center",
                  }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#0F1419", lineHeight: 1.45, marginBottom: 10 }}>
                      {wtrSyn.line}
                    </div>
                    {/* Confidence tiers (founder ruling 2026-07-11): the
                        measured TDS reading is a submersion-gated,
                        median-stabilized hardware measurement, tier
                        "verified". The RO projection quoted in the line
                        above is model output, tier "developing", badged
                        separately so the measurement's credibility never
                        rubs off on the projection. Both badges render only
                        for gated tests; a tier without provenance is
                        decoration, and decoration is how an empty bottle
                        scored 89. */}
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, flexWrap: "wrap" }}>
                      <span style={{
                        display: "inline-block",
                        border: `1.5px solid ${col}`,
                        color: col,
                        borderRadius: 999,
                        padding: "4px 14px",
                        fontSize: 11,
                        fontWeight: 700,
                      }}>
                        verified
                      </span>
                      {wtrSyn.projected && (
                        <span style={{
                          display: "inline-block",
                          border: "1.5px solid #A6A8AB",
                          color: "#A6A8AB",
                          borderRadius: 999,
                          padding: "4px 12px",
                          fontSize: 11,
                          fontWeight: 700,
                        }}>
                          projection: developing
                        </span>
                      )}
                      {testedAtLabel && (
                        <span style={{ fontSize: 11, color: "#A6A8AB", fontWeight: 600 }}>
                          {testedAtLabel}
                        </span>
                      )}
                    </div>
                    {isLive && !submerged && (
                      <div style={{ fontSize: 11, color: "#A6A8AB", marginTop: 8, lineHeight: 1.5 }}>
                        Bottle is below the test level. Fill and run a fresh test to update this result.
                      </div>
                    )}
                  </div>
                )}
                {sourcePromptTs != null && lastTest && lastTest.ts === sourcePromptTs && (
                  <SourceTaggingCard
                    apiBase={API_BASE}
                    test={lastTest}
                    onTag={(source, brand, attributionMode) => tagSource(sourcePromptTs, source, brand, attributionMode)}
                    onOpenScanner={(brandKey, live) => { if (onOpenBottleScan) onOpenBottleScan(brandKey, live); }}
                    onClose={() => setSourcePromptTs(null)}
                  />
                )}
                {showCompare && (
                  <CompareMyWatersScreen
                    apiBase={API_BASE}
                    onClose={() => setShowCompare(false)}
                  />
                )}
              </>
            ) : (
              <div style={{textAlign:"center",padding:"20px 0"}}>
                <div style={{fontSize:13,color:"#A6A8AB",marginBottom:4}}>
                  {testHasRun ? "Fill the bottle to its line to read water quality" : "Run a water test to see your score"}
                </div>
              </div>
            )}

            {/* Run Test button, compact, always visible when not testing */}
            {testPhase === null && (
              <button onClick={runTest} style={{
                margin: "12px auto 0", display: "flex", alignItems: "center", gap: 8,
                background: "linear-gradient(135deg, #51B0E6, #2B8FC9)", color: "#fff",
                border: "none", padding: "10px 24px", borderRadius: 30,
                fontSize: 12, fontWeight: 800, cursor: "pointer",
                boxShadow: "0 4px 16px rgba(81,176,230,0.35)",
                letterSpacing: "0.3px",
              }}>
                <BtlIcon name="flask" size={14} color="#FFFFFF" /> {testHasRun ? "TEST AGAIN" : "RUN WATER TEST"}
              </button>
            )}

            {/* Submersion gate feedback: why the test did not run. Plain
                statement, no score, no tier, nothing to screenshot as a
                result. Auto-clears. */}
            {testGateMsg && (
              <div style={{
                margin: "10px auto 0", maxWidth: 320, textAlign: "center",
                fontSize: 12, fontWeight: 600, color: "#C89B3C", lineHeight: 1.5,
              }}>
                {testGateMsg}
              </div>
            )}

            {/* Water Rankings entry: visible once any test history exists.
                (v2.1: retitled from "My Waters"; same overlay.) */}
            {testPhase === null && testHistory.length > 0 && (
              <button onClick={() => setShowCompare(true)} style={{
                margin: "10px auto 0", display: "block",
                background: "transparent", color: "#51B0E6",
                border: "none", padding: "6px 12px",
                fontSize: 11.5, fontWeight: 800, cursor: "pointer",
                letterSpacing: "0.3px", fontFamily: "inherit",
              }}>
                WATER RANKINGS: SEE HOW THIS COMPARES
              </button>
            )}

            {/* Quick stats row below ring */}
            <div style={{
              display: "flex", justifyContent: "center", gap: 24,
              marginTop: 20, paddingTop: 16,
              borderTop: "1px solid rgba(15, 20, 25, 0.06)",
            }}>
              {[
                // TDS here reads as CURRENT state (it sits beside live Level
                // and Temp), so it is submersion-gated (2026-07-11): a value
                // renders only while there is enough water for the electrode
                // to be reading anything real. The last test result lives in
                // the ring above, with its timestamp. When disconnected,
                // this row shows the last test value explicitly as history
                // via the ring; the row itself dashes out.
                { label: "TDS", val: isLive && submerged && hasResult && displayTds != null && displayTds > 0 ? `${displayTds}` : "\u2014", unit: "ppm", c: col },
                { label: "Level", val: levelDisplayMl ?? "\u2014", unit: "mL", c: inBottleColor() },
                { label: "Temp", val: tempF ?? "\u2014", unit: "\u00B0F", c: "#C89B3C" },
              ].map((m, i) => (
                <div key={i} style={{ textAlign: "center", minWidth: 60 }}>
                  <div style={{ fontSize: 9, fontWeight: 700, color: "#A6A8AB", letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 4, whiteSpace: "nowrap" }}>
                    {m.label}
                  </div>
                  <div style={{ display: "flex", alignItems: "baseline", justifyContent: "center", gap: 2 }}>
                    <span style={{ fontSize: 22, fontWeight: 800, color: "#0F1419", fontVariantNumeric: "tabular-nums" }}>
                      {m.val}
                    </span>
                    {m.unit && <span style={{ fontSize: 10, color: "#A6A8AB", fontWeight: 600 }}>{m.unit}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── SECTION 3: Telemetry Cards (3-up) ──
              Plausibility-gated (2026-07-07): the defective firmware volume
              (0x06) never renders; IN BOTTLE is computed from the ToF level.
              Real-hardware temp renders as Calibrating until the firmware
              unit fix (defect 1). */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 14 }}>
            {/* TDS telemetry card, submersion-gated (2026-07-11): this card
                sits in the LIVE row, so it may never show a value or a
                checkmark for water that is not in the bottle. Empty bottle:
                "Awaiting water", full stop. The 62 ppm ✓ Good that survived
                a drained bottle for 30 minutes came from this card. */}
            <MetricCard
              icon={<BtlIcon name="droplet" size={15} color={isLive && submerged ? col : "#A6A8AB"} />}
              label="TDS"
              value={isLive && submerged && hasResult && displayTds != null && displayTds > 0 ? displayTds : null}
              unit="ppm"
              color={isLive && submerged ? col : "#A6A8AB"}
              hist={isLive && submerged && testHasRun ? histOf("tds") : []}
              sublabel={
                isLive && submerged
                  ? (hasResult && displayScore != null ? `${displayScore >= 80 ? "\u2713" : "\u26A0"} ${scoreBandWord(displayScore)}` : "Run a test")
                  : isLive
                  ? "Awaiting water"
                  : "Connect bottle"
              }
            />
            <MetricCard
              icon={<BtlIcon name="ruler" size={15} color={inBottleColor()} />}
              label="Level"
              value={levelDisplayMl}
              unit="mL"
              color={inBottleColor()}
              hist={inBottleHist}
              sublabel={levelDisplayMl != null ? (levelDisplayMl < 100 ? "\u26A0 Refill soon" : "\u2713 Ready") : null}
            />
            <MetricCard
              icon={<BtlIcon name="thermometer" size={15} color="#C89B3C" />}
              label="Temp"
              value={tempF}
              unit={"\u00B0F"}
              color="#C89B3C"
              hist={isDemo ? histOf("waterTemp") : []}
              sublabel={tempF != null ? (parseFloat(tempF) > 86 ? "\u26A0 Warm" : "\u2713 Cool") : "Calibrating"}
            />
          </div>

          {/* ── SECTION 6: Contaminant Alerts (pinned to the last test) ── */}
          {hasResult && displayTds != null && displayTds > 150 && (
            <div className="card-attention" style={{
              padding: "18px 16px",
              marginBottom: 14,
            }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#C89B3C", letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 12 }}>
                DETECTED CONCERNS
              </div>
              <div style={{
                display: "flex", alignItems: "center", gap: 12,
                background: "rgba(255, 255, 255, 0.55)",
                backdropFilter: "blur(20px) saturate(180%)",
                WebkitBackdropFilter: "blur(20px) saturate(180%)",
                borderRadius: 14, padding: "14px 16px",
                border: "1px solid rgba(200, 155, 60, 0.18)",
              }}>
                <BtlIcon name="hazard" size={20} color="#C89B3C" />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#0F1419" }}>
                    Elevated TDS ({displayTds} ppm)
                  </div>
                  <div style={{ fontSize: 11, color: "#3D4043", lineHeight: 1.4, marginTop: 2 }}>
                    Consider using a reverse osmosis or activated carbon filter.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── SECTION 7: Daily Hydration (the SHARED card, identical to My Hydration) ── */}
          <div style={{ marginBottom: 14 }}>
            <DailyHydrationCard
              label="Daily Hydration"
              dayMl={dayMl}
              goalMl={goalDayMl}
              verifiedMl={dailyVerifiedMl}
              live={dailyLive}
              footnote="Absorption, not just intake: the headline is absorbed hydration, matching My Hydration. The green figure is raw intake your bottle verified, so it can read slightly higher than the absorbed total."
            />
          </div>

          {/* ── SECTION 6.5: Absorption Intelligence ──
              Live reading rides absorptionLive (last completed test), so this
              card refetches once per test, never per telemetry frame. */}
          <div style={{ marginBottom: 14 }}>
            <AbsorptionIntelligenceCard apiBase={API_BASE} liveReading={absorptionLive} />
          </div>

          {/* ── SECTION 8: Water Quality History (replaces the dev History/Sensors/Debug tabs) ── */}
          <TestHistoryCard history={testHistory} />

          {/* ── SECTION 8.5: Water Rankings (v2.1, Jul 14 2026) ──
              Compact board read directly below Water Quality History.
              Server-shaped by tier (middle-path gate): plus sees the top
              of the full board, free sees the top source plus a locked
              count. Tapping any row or SEE ALL opens the full Water
              Rankings screen (the CompareMyWatersScreen overlay). */}
          <WaterRankingsCard
            apiBase={API_BASE}
            onOpenRankings={() => setShowCompare(true)}
          />

          {/* ── SECTION: Cap Close-up ── */}
          <div style={{
            borderRadius: 20, overflow: "hidden", marginBottom: 14,
            boxShadow: "0 2px 12px rgba(15, 20, 25, 0.08)",
          }}>
            <div style={{ position: "relative" }}>
              <img src="/bottle-cap-exploded.jpg" alt="Smart Cap Sensor"
                style={{ width: "100%", height: 160, objectFit: "cover", display: "block" }} />
              <div style={{
                position: "absolute", inset: 0,
                background: "linear-gradient(180deg, transparent 40%, rgba(15,20,25,0.8) 100%)",
              }} />
              <div style={{
                position: "absolute", bottom: 14, left: 16,
                color: "#FFFFFF",
              }}>
                <div style={{ fontSize: 14, fontWeight: 700 }}>Intelligent Smart Cap</div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.7)" }}>
                  Real-time TDS, Intake, and temperature sensors
                </div>
              </div>
            </div>
          </div>

          {/* ═══════════════════════════════════════════════════════
              SENSOR DEBUG PANEL (dev branch, connected only)
              Retained through the day-long soak validation; removal PR
              afterward keeps the RX resync fix and checksum logging.
              Never renders in demo mode: gate is ble === "connected",
              and connect() kills any running demo, so every number
              here came off the wire.
              ═══════════════════════════════════════════════════════ */}
          {ble === "connected" && (
            <div className="card-default" style={{ padding: "18px 16px", marginBottom: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: showDebug ? 14 : 0 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#B84A4A", letterSpacing: 1.5, textTransform: "uppercase" }}>
                  Sensor Debug (dev branch)
                </div>
                <button onClick={() => setShowDebug(v => !v)} style={{
                  background: "rgba(15, 20, 25, 0.04)", border: "1px solid rgba(15, 20, 25, 0.06)",
                  borderRadius: 10, padding: "6px 14px", fontSize: 11, fontWeight: 700,
                  color: "#3D4043", cursor: "pointer",
                }}>
                  {showDebug ? "Hide" : "Show"}
                </button>
              </div>

              {showDebug && (
                <div style={{ fontFamily: "Menlo, 'SF Mono', Consolas, monospace" }}>

                  {/* Missing expected sensors banner */}
                  {dbgMissing.length > 0 && (
                    <div style={{
                      background: "rgba(184, 74, 74, 0.08)", border: "1px solid rgba(184, 74, 74, 0.25)",
                      borderRadius: 10, padding: "10px 12px", marginBottom: 12,
                      fontSize: 11, fontWeight: 700, color: "#B84A4A", lineHeight: 1.6,
                    }}>
                      {dbgMissing.map(e => (
                        <div key={e.type}>{hexType(e.type)} {e.name}: MISSING FROM ALL FRAMES</div>
                      ))}
                    </div>
                  )}

                  {/* Last parsed frame */}
                  <div style={{ fontSize: 9, fontWeight: 700, color: "#A6A8AB", letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 6 }}>
                    Last Parsed Frame
                  </div>
                  {tele ? (
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 10, color: "#3D4043", marginBottom: 6 }}>
                        state={tele.state} batt={tele.batt}% charging={String(tele.charging)} fw={tele.fw}
                      </div>
                      {Object.values(tele.sensors).sort((a, b) => a.type - b.type).map(sx => (
                        <div key={sx.type} style={{
                          display: "flex", justifyContent: "space-between", gap: 8,
                          fontSize: 10, color: "#0F1419", padding: "4px 0",
                          borderBottom: "1px solid rgba(15,20,25,0.05)",
                        }}>
                          <span style={{ color: "#51B0E6", fontWeight: 700, minWidth: 36 }}>{hexType(sx.type)}</span>
                          <span style={{ flex: 1 }}>{sx.label}</span>
                          <span style={{ fontWeight: 700 }}>raw={sx.raw}</span>
                          <span style={{ color: "#3D4043" }}>val={sx.val}{sx.unit}</span>
                          <span style={{ color: sx.status === 0 ? "#4A8A6F" : "#C89B3C" }}>st={sx.status}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ fontSize: 11, color: "#A6A8AB", marginBottom: 12 }}>Waiting for first frame</div>
                  )}

                  {/* Presence and drift summary */}
                  <div style={{ fontSize: 9, fontWeight: 700, color: "#A6A8AB", letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 6 }}>
                    Presence and Drift ({hist.length} frames)
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    {Object.values(dbgStats).sort((a, b) => a.type - b.type).map(st => (
                      <div key={st.key} style={{
                        fontSize: 10, color: "#0F1419", padding: "4px 0",
                        borderBottom: "1px solid rgba(15,20,25,0.05)",
                      }}>
                        <span style={{ color: "#51B0E6", fontWeight: 700 }}>{hexType(st.type)}</span>
                        {" "}{st.label}: {st.count}/{hist.length} frames, last={st.last}, min={st.min}, max={st.max},{" "}
                        <span style={{ fontWeight: 700, color: (st.max - st.min) > 0 ? "#C89B3C" : "#4A8A6F" }}>
                          span={st.max - st.min}
                        </span>
                      </div>
                    ))}
                    {Object.keys(dbgStats).length === 0 && (
                      <div style={{ fontSize: 11, color: "#A6A8AB" }}>No sensor history yet</div>
                    )}
                  </div>

                  {/* Copy report */}
                  <button onClick={copyDebugReport} style={{
                    width: "100%", padding: "10px 0", marginBottom: 8,
                    background: dbgCopied ? "#4A8A6F" : "#51B0E6", color: "#FFFFFF",
                    border: "none", borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: "pointer",
                  }}>
                    {dbgCopied ? "Copied" : "Copy Debug Report"}
                  </button>
                  <div style={{ fontSize: 9, color: "#A6A8AB", marginBottom: 6 }}>
                    If Copy fails in Bluefy, select the text below by hand.
                  </div>
                  <textarea
                    readOnly
                    value={dbgReport}
                    style={{
                      width: "100%", height: 140, fontSize: 9,
                      fontFamily: "Menlo, 'SF Mono', Consolas, monospace",
                      border: "1px solid rgba(15,20,25,0.10)", borderRadius: 10,
                      padding: 8, color: "#0F1419", background: "rgba(15,20,25,0.02)",
                      resize: "vertical", marginBottom: 12,
                    }}
                  />

                  {/* Raw log */}
                  <div style={{ fontSize: 9, fontWeight: 700, color: "#A6A8AB", letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 6 }}>
                    Raw Log (newest first)
                  </div>
                  <div style={{ maxHeight: 220, overflowY: "auto" }}>
                    {log.slice(0, 25).map((e, i) => (
                      <div key={i} style={{
                        fontSize: 9, padding: "3px 0", lineHeight: 1.5,
                        borderBottom: "1px solid rgba(15,20,25,0.04)",
                        color: e.msg.includes("FRAME") ? "#0F1419" : "#A6A8AB",
                        fontWeight: e.msg.includes("FRAME") ? 700 : 400,
                        wordBreak: "break-all",
                      }}>
                        [{e.t}] {e.msg}{e.hex ? " " + e.hex : ""}
                      </div>
                    ))}
                    {log.length === 0 && (
                      <div style={{ fontSize: 11, color: "#A6A8AB" }}>No log entries yet</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

        </div>
      )}
    </div>
  );
}
